import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import DefaultModal from '../../../../../components/DefaultModal'
import FormButton from '../../../../../components/FormButton'
import { COMPANY_ADDRESS_INLINE } from '../../../../../constants/company'
import { RootState } from '../../../../../redux/store'

interface ClientAgreementModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  isLoading: boolean
}

const ClientAgreementModal: React.FC<ClientAgreementModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  isLoading,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const user = useSelector((state: RootState) => state.session.user)

  const handleScroll = () => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    // Consider scrolled to bottom when user is within 20px of the bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
    }
  }

  // Reset scroll state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setHasScrolledToBottom(false)
    }
  }, [isOpen])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      inlineLoading
      title="Client Agreement"
      maxW="900px"
    >
      <div
        ref={scrollContainerRef}
        className="p-8 max-h-[70vh] overflow-y-auto"
        onScroll={handleScroll}
      >
        <div className="text-description-large font-light">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-4">CLIENT AGREEMENT</h1>
            <p className="mb-4">Effective Between:</p>
            <p className="mb-1">Penning Financial Services ApS</p>
            <p className="mb-1">CRN: DK44645971</p>
            <p className="mb-4">Registered Address: {COMPANY_ADDRESS_INLINE}</p>
            <p className="mb-1">(Hereinafter referred to as "Penning")</p>
            <p className="mb-1">and</p>
            <p className="mb-4">{user.name}</p>
            <p>(Hereinafter referred to as "the Client")</p>
          </div>

          <h2 className="text-xl font-semibold mt-8 mb-4">Introduction</h2>
          <p className="mb-4">
            Welcome to Penning Financial Services ApS. This Client Agreement
            ("Agreement") sets out the terms and conditions governing the client
            relationship between Penning and each individual or entity approved
            through Penning's onboarding process.
          </p>
          <p className="mb-4">
            By completing the onboarding and accessing Penning's services, the
            Client confirms having read, understood, and accepted all the terms
            set forth herein. This Agreement forms the legal foundation of the
            Client's engagement with Penning.
          </p>
          <p className="mb-4">
            Penning offers its services under applicable Danish and European
            regulations, including the Markets in Crypto-Assets Regulation
            (Regulation (EU) 2023/1114, "MiCA").
          </p>

          <p className="mb-4">
            This Agreement applies to all clients who use any of the regulated
            solutions made available by Penning Financial Services ApS,
            including but not limited to:
          </p>

          <ul className="list-disc pl-8 mb-4">
            <li>Investment Packages and portfolio management strategies</li>
            <li>Broker Services (customer-directed buy/sell orders)</li>
            <li>Custodial Service</li>
          </ul>

          <p className="mb-4">
            These solutions may be used individually or in combination and are
            subject to the same client obligations, risk disclosures, and
            disclaimers set forth in this Agreement.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            Important informations
          </h2>

          <h3 className="text-lg font-medium mt-6 mb-3">About the agreement</h3>
          <p className="mb-4">
            This Agreement governs the provision of products, services, and
            investment packages offered by Penning Financial Services ApS
            ("Penning"). The Agreement applies to all fully onboarded, verified,
            and approved clients of Penning.
          </p>
          <p className="mb-4">
            Penning is registered with and authorized by the Danish Financial
            Supervisory Authority and operates in accordance with the Markets in
            Crypto-Assets Regulation (Regulation (EU) 2023/1114, "MiCA"). All
            services described in this Agreement are authorized and available to
            clients.
          </p>
          <p className="mb-4">
            This Agreement does not constitute a public offer to buy shares in
            Penning, its partners, subsidiaries, or affiliated companies, nor is
            it any form of investment or trading recommendation or advice. It is
            not available to U.S. persons or entities due to regulatory
            restrictions.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            Confidentiality and Privacy
          </h3>
          <p className="mb-4">
            Any distribution of this agreement without Penning's approval is
            strictly prohibited and may have legal consequences for the Client.
            The information contained in this Agreement, including details on
            the investment packages and portfolio management strategies, is
            confidential and proprietary to Penning.
          </p>
          <p className="mb-4">
            All information described in this Agreement, including the
            strategies and performance metrics of the investment packages and
            services, must not be disclosed, in writing, orally, or in any other
            form without Penning's prior written approval. It is strictly
            prohibited to copy, reproduce, or manipulate this agreement in any
            way.
          </p>
          <p className="mb-4">
            All recipients are required to adhere to confidentiality obligations
            regarding the contents of this Agreement, including but not limited
            to any information related to the packages and services presented
            herein, as well as any personal or investment-related data
            concerning the Client.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            Risk Acknowledgement
          </h3>
          <p className="mb-4">
            The Client acknowledges that investments in digital assets, in using
            Penning's broker services, and portfolio management services,
            involves significant risk. These investments are speculative in
            nature and may result in the loss of all or part of the invested
            capital.
            Penning does not guarantee any returns on investments and the value
            of the Client's portfolio may fluctuate depending on market
            conditions. The Client is fully responsible for assessing the
            suitability of these investments based on their risk tolerance and
            financial objectives. By entering into this Agreement, the Client
            affirms that they have a clear understanding of the risks involved
            and have been provided the opportunity to ask questions regarding
            the investment packages offered by Penning.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">1. Definitions</h2>
          <p className="mb-2">
            1.1 For the purposes of this Agreement, the following definitions
            apply:
          </p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>
              <strong>Penning</strong> means Penning Financial Services ApS, a
              company registered in Denmark under CRN DK44645971, with its
              principal office at {COMPANY_ADDRESS_INLINE}.
            </li>
            <li>
              <strong>Client</strong> means the natural or legal person who has
              completed the onboarding process and entered into this Agreement
              with Penning.
            </li>
            <li>
              <strong>Agreement</strong> means this client agreement, including
              any annexes, appendices, and future amendments.
            </li>
            <li>
              <strong>Portfolio Management</strong> means discretionary
              management of the Client's digital assets through predefined
              investment packages offered by Penning.
            </li>
            <li>
              <strong>Broker Service</strong> means the execution of
              crypto-asset transactions on behalf of the Client via selected
              third-party liquidity providers.
            </li>
            <li>
              <strong>Custody Service</strong> means the safekeeping and
              administration of crypto-assets on behalf of the Client, provided
              as an authorized service under Penning's CASP license.
            </li>
            <li>
              <strong>Investment Packages</strong> means the predefined
              portfolio management strategies and service offerings provided by
              Penning.
            </li>
            <li>
              <strong>MiCA</strong> means the Markets in Crypto-Assets
              Regulation (Regulation (EU) 2023/1114).
            </li>
          </ul>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            2. Basis of the agreement
          </h2>
          <p className="mb-4">
            2.1 This Agreement establishes the legal and operational framework
            for the relationship between Penning Financial Services ApS
            ("Penning") and the Client. It governs the provision of one or more
            of the following services:
          </p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>discretionary portfolio management and investment packages,</li>
            <li>brokerage of crypto-assets, and/or</li>
            <li>custody services for crypto-assets on behalf of the Client.</li>
          </ul>
          <p className="mb-4">
            These services are offered individually or in combination, depending
            on the Client's preferences and scope.
          </p>
          <p className="mb-4">
            2.1 By entering into this Agreement, the Client authorizes Penning
            to act on their behalf within the selected service(s) and
            acknowledges that the Agreement is binding under the applicable
            terms and regulatory framework.
          </p>
          <p className="mb-4">
            Penning offers its services in accordance with its authorizations as
            a Crypto-Asset Service Provider (CASP). All products and services
            described in this Agreement are active and provided in full
            compliance with applicable regulatory requirements.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">3. About Penning</h2>
          <h3 className="text-lg font-medium mt-6 mb-3">
            3.1 Company Overview
          </h3>
          <p className="mb-4">
            3.1.1 Penning Financial Services ApS ("Penning") is a Danish company
            that provides digital asset services through structured and
            technology-driven solutions. Penning is committed to full compliance
            with applicable European financial regulation, including the Markets
            in Crypto-Assets Regulation (EU) 2023/1114 ("MiCA"). Penning is
            authorized by the Danish Financial Supervisory Authority as a
            Crypto-Asset Service Provider (CASP) and operates in accordance with
            applicable regulatory requirements.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">3.2 Service Scope</h3>
          <p className="mb-4">
            3.2.1 Penning offers a range of crypto-asset services designed to
            support Clients in managing, trading, and safeguarding their digital
            assets. These services include:
          </p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>
              <strong>
                Discretionary portfolio management of digital assets
              </strong>
              , offered through predefined investment packages tailored to
              various risk profiles.
            </li>
            <li>
              <strong>Brokerage services</strong> for the execution of client
              orders on their behalf in the crypto-market.
            </li>
            <li>
              <strong>Custody services</strong>, including the safekeeping and
              administrative handling of crypto-assets on behalf of clients.
            </li>
          </ul>
          <p className="mb-4">
            Each of these services is designed with a focus on risk control,
            client transparency, and adherence to best practices in the digital
            investment space. Penning may update or expand these services in
            accordance with its regulatory permissions and operational
            developments.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            3.3 Regulatory Commitment
          </h3>
          <p className="mb-4">
            3.3.1 Penning's service model is developed in accordance with the
            governance and Client protection requirements under MiCA. Where
            relevant, specific activities are subject to regulatory
            authorisation and oversight. Penning is committed to maintaining the
            necessary internal policies, operational safeguards, and client
            protections to meet evolving legal and compliance standards.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            3.4 Investment Philosophy
          </h3>
          <p className="mb-4">
            3.4.1 Penning's investment approach is centered on accessibility,
            risk alignment, and transparency. Rather than offering bespoke
            portfolios, Penning provides access to standardized investment
            packages tailored in such a way that it allows the Client to suit
            their specific profile, market exposure preferences, and risk
            appetite.
          </p>
          <p className="mb-4">
            Capital is allocated per package into individually segregated
            sub-accounts, maintained at selected third-party custodian or
            exchange partners. This setup ensures full traceability,
            package-specific performance measurement, and clear separation
            between client assets. The packages combine structured exposure to
            digital markets with real-time portfolio monitoring and periodic
            reporting to clients.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            4. Portfolio Management
          </h2>
          <h3 className="text-lg font-medium mt-6 mb-3">
            4.1 Portfolio Management Model and Investment Packages
          </h3>
          <p className="mb-4">
            4.1.2 Penning's portfolio management framework is based on a
            structure of predefined investment packages tailored to different
            risk profiles and return expectations. Clients may allocate funds to
            one or more packages. All services are offered under a discretionary
            portfolio management model, where Penning retains full authority to
            manage a portfolio in accordance with the selected package(s).
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            4.2 Capital Contribution and Setup
          </h3>
          <p className="mb-4">
            4.2.1 Investments must be made in stablecoins (e.g., USDC). Clients
            who do not already hold stablecoins may acquire them via Penning's
            broker service or through external vendors. Broker-service fees may
            apply.
          </p>
          <p className="mb-4">
            4.2.2 The minimum investment requirements for each package are as
            specified on Penning's digital platform or as otherwise agreed with
            the Client.
          </p>
          <p className="mb-4">
            4.2.3 Before funds can be allocated, the Client must complete
            Penning's onboarding process, including KYC/KYB and AML screening.
            Penning reserves the right to request updated documentation or
            re-initiate onboarding at any time.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            4.3 Custody and Asset Segregation
          </h3>
          <p className="mb-4">
            4.3.1 All Client assets are held in individually segregated
            subaccounts with reputable third-party custodians. These accounts
            are linked to the Client's user profile and reflect the Client's
            beneficial ownership.
          </p>
          <p className="mb-4">
            4.3.2 Penning does not pool Client funds into collective vehicles or
            shared accounts. Each Client's portfolio is managed independently in
            accordance with the selected package(s).
          </p>
          <p className="mb-4">
            4.3.3 Penning retains full discretionary authority to manage the
            subaccounts within the scope of this Agreement, including
            allocation, rebalancing, and risk management activities.
          </p>
          <p className="mb-4">
            4.3.4 All custody services are facilitated through third-party
            providers selected by Penning based on commercial, technical, and
            regulatory criteria. These third parties are subject to ongoing due
            diligence, but Penning does not guarantee their performance or
            financial standing.
          </p>
          <p className="mb-4">
            4.3.5 Penning takes commercially reasonable steps to ensure the
            safekeeping of Client assets and to prevent co-mingling with its own
            funds or other clients' assets. These custody arrangements are
            designed to meet the safeguarding requirements under Article 75 of
            the Markets in Crypto-Assets Regulation (MiCA) and Article 12 of RTS
            2025/305.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            4.4 Performance-Based Fees and Other Expenses
          </h3>
          <p className="mb-4">
            4.4.1 Penning charges a performance-based monthly management fee for
            each selected package, applied only if a positive return is
            achieved.
          </p>
          <p className="mb-4">4.4.2 The fee structure is:</p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>
              Performance-based fees as defined in the fee schedule applicable to
              the selected package.
            </li>
            <li>
              Tiered management fees based on the total portfolio value as
              specified in current pricing documentation.
            </li>
          </ul>
          <p className="mb-4">
            4.4.3 Fees are calculated independently for each package and
            deducted automatically in stablecoins at the end of each month. No
            High-Water Mark or cumulative performance threshold is applied.
          </p>
          <p className="mb-4">
            4.4.4 Additional operational costs, such as trading spreads,
            exchange fees, custody setup, transaction execution, and portfolio
            rebalancing, may be deducted from the Client's portfolio and are
            factored into the reported performance.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            4.5 Lock-up and Withdrawal
          </h3>
          <p className="mb-4">
            4.5.1 Invested funds are subject to a 24-hour lock-up period from
            the time of activation.
          </p>
          <p className="mb-4">
            4.5.2 Withdrawals require 24 hours' prior notice and must be made to
            a whitelisted wallet associated with the Client's Penning profile.
            Withdrawal requests will be processed within 24 hours, however,
            payouts to the Client can take up to 6 (six) working days.
          </p>
          <p className="mb-4">
            4.5.3 If the Client prefers withdrawal in fiat currency, such
            withdrawal must be conducted through Penning's broker service, and
            standard broker fees will apply as defined in the current fee
            schedule.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            4.6 Portfolio Returns
          </h3>
          <p className="mb-4">
            4.6.1 Returns are automatically reinvested into the selected
            investment package to benefit from the effects of compounding.
          </p>
          <p className="mb-4">
            4.6.2 The Client may request withdrawal of returns under the same
            notice and withdrawal procedures outlined in section 4.4.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            4.7 Client Access and Portfolio Monitoring
          </h3>
          <p className="mb-4">
            4.7.1 Clients can monitor their portfolio value, allocations, and
            returns in real time via Penning's digital dashboard.
          </p>
          <p className="mb-4">
            4.7.2 Periodic portfolio reports are made available, subject to
            operational availability.
          </p>
          <p className="mb-4">
            4.7.3 In periods of high market volatility, maintenance, or system
            upgrades, portfolio reporting may be delayed but will resume as soon
            as feasible.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            5. Risk Acknowledgment
          </h2>
          <p className="mb-4">
            5.1 The risks outlined in this section are not exhaustive. Other
            risks—whether currently unknown or assessed as immaterial—may arise
            and significantly impact Penning's business operations, financial
            position, or the performance of its investment packages. The
            sequence of risk items does not reflect their likelihood or
            severity. Only Clients who fully understand these risks and who are
            financially able to bear the full loss of their investment should
            proceed under this Agreement.
          </p>
          <p className="mb-4">
            5.2 Penning operates in a dynamic and evolving market. While Penning
            maintains ongoing risk management and compliance functions in line
            with applicable regulations, including the Markets in Crypto-Assets
            Regulation (EU) 2023/1114 ("MiCA"), it is not possible to eliminate
            all risks—especially those related to technology, custody,
            third-party infrastructure, or legal frameworks.
          </p>
          <p className="mb-4">
            5.3 The Client acknowledges that no guarantee of performance,
            preservation of capital, or protection against volatility has been
            given. Any projections, illustrations, or communications from
            Penning or its affiliates are for informational purposes only and
            shall not be interpreted as a commitment or financial advice.
          </p>
          <p className="mb-4">
            5.4 Digital assets may be subject to high price volatility. Their
            valuation is not based on standardized indices or clearing
            mechanisms, and pricing may vary between platforms or over time.
          </p>
          <p className="mb-4">
            5.5 The underlying technologies supporting crypto-assets, including
            blockchain and distributed ledger technology (DLT), are developing
            rapidly. The Client accepts that protocol failures, cyberattacks,
            bugs, and outages at third-party service providers may occur and
            negatively affect asset value or accessibility.
          </p>
          <p className="mb-4">
            5.6 Custody of digital assets involves inherent risks, such as key
            mismanagement, cybersecurity breaches, or insolvency of third-party
            custodians. Although Penning selects reputable custody providers,
            the Client remains ultimately responsible for the investment risk.
          </p>
          <p className="mb-4">
            5.7 Regulatory developments—including those under MiCA or other
            EU/local laws—may affect how digital assets are treated, taxed, or
            transacted. Such changes may impact both the value of the Client's
            portfolio and Penning's ability to provide services.
          </p>
          <p className="mb-4">
            5.8 By entering into this Agreement, the Client grants Penning
            discretionary authority to manage their assets in accordance with
            the selected investment package(s) and services. The Client accepts
            full responsibility for all investment outcomes, including partial
            or full loss of capital.
          </p>
          <p className="mb-4">
            5.9 The Client confirms that they have independently assessed the
            suitability of this investment based on their risk tolerance,
            financial situation, and investment goals, and that they are able to
            absorb a total loss of capital.
          </p>
          <p className="mb-4">
            5.10 Except in cases of willful misconduct or gross negligence, the
            Client waives the right to hold Penning liable for any losses
            arising from market performance, custody failure, or third-party
            service disruptions.
          </p>
          <p className="mb-4">
            5.11 The Client acknowledges that all risk disclosures, disclaimers,
            and liability limitations described in this Agreement apply equally
            to both discretionary investments in predefined packages and to
            individual transactions initiated by the Client via Broker Services.
            All trading and investment activity is undertaken at the Client's
            sole risk, regardless of execution method.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            6. Internet and electronic Infrastructure
          </h2>
          <p className="mb-4">
            6.1 Penning shall not be liable for losses, delays, errors, or
            damages caused directly or indirectly by:
          </p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>
              Failures in internet communication, data signals, or third-party
              routing systems.
            </li>
            <li>
              Errors or malfunctions in computer facilities, blockchain
              networks, or trading platforms.
            </li>
            <li>
              Actions or inactions of external service providers, including
              exchanges, liquidity providers, banks, or custodians.
            </li>
            <li>
              Unauthorized access or cyberattacks by third parties, including
              hacking, phishing, or data breaches.
            </li>
          </ul>
          <p className="mb-4">
            6.2 Although Penning undertakes commercially reasonable efforts to
            ensure operational integrity and security—including reliance on
            reputable third-party infrastructure—complete risk elimination is
            not possible. The Client accepts these inherent limitations and
            bears the associated risks.
          </p>
          <p className="mb-4">
            6.3 Penning maintains appropriate technical and organizational
            measures, including periodic stress tests and IT security policies,
            in line with its regulatory obligations under the Markets in
            Crypto-Assets Regulation (MiCA).
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            7. Declaration of Competence
          </h2>
          <p className="mb-4">
            7.1 By entering into this Agreement and throughout its duration, the
            Client represents, warrants, and undertakes the following:
          </p>
          <p className="mb-4">
            7.1.1 There is no legal restriction, court ruling, law, or
            regulatory obligation that prohibits the Client from entering into
            or fulfilling obligations under this Agreement.
          </p>
          <p className="mb-4">
            7.1.2 The Client has obtained all necessary consents and holds full
            legal capacity to enter into this Agreement. If the Client is a
            legal entity, it confirms that all required internal approvals,
            including board or shareholder resolutions, have been properly
            obtained in accordance with applicable bylaws and governance rules.
          </p>
          <p className="mb-4">
            7.1.3 The capital invested under this Agreement is not subject to
            any lien, pledge, restriction, or encumbrance and is fully available
            for discretionary portfolio management, broker services, or
            custodianship by Penning.
          </p>
          <p className="mb-4">
            7.1.4 The Client is financially capable of absorbing the full loss
            of the invested amount without compromising their financial
            stability or violating obligations towards creditors or other third
            parties.
          </p>
          <p className="mb-4">
            7.1.5 The information provided to Penning during onboarding or
            thereafter is, to the best of the Client's knowledge, true,
            accurate, complete, and not misleading in any material respect.
          </p>
          <p className="mb-4">
            7.1.6 The Client acknowledges that this declaration forms part of
            Penning's regulatory obligations, including those under the Markets
            in Crypto-Assets Regulation (MiCA), and may be relied upon in
            relation to compliance with anti-money laundering (AML), Client
            protection, and risk assessment requirements.
          </p>
          <p className="mb-4">
            7.2 The Client shall notify Penning without undue delay if any of
            the statements in Section 7.1 become inaccurate or invalid. Penning
            reserves the right to suspend or terminate this Agreement with
            immediate effect if continued cooperation becomes unlawful or
            materially compromised.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            8. Conflicts of Interest
          </h2>
          <p className="mb-4">
            8.1 The Client acknowledges that Penning may, from time to time,
            receive fees, rebates, or other financial incentives from
            third-party service providers, including exchanges or custodians,
            based on trading volumes, partnership arrangements, or other
            performance-related criteria.
          </p>
          <p className="mb-4">
            8.2 Penning maintains a conflict of interest policy in accordance
            with Article 72 of the Markets in Crypto-Assets Regulation (MiCA)
            and undertakes reasonable efforts to identify, manage, and mitigate
            any potential or actual conflicts that may arise in the course of
            providing its services. A summary of this policy is available upon
            request.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            9. Indemnification and Limitation of Liability
          </h2>
          <p className="mb-4">
            9.1 The Client is solely responsible for ensuring compliance with
            all applicable laws, regulations, and tax obligations in their
            jurisdiction of residence, incorporation, or taxation. The Client
            shall promptly inform Penning of any legal changes that may affect
            the validity or legality of this Agreement or the Client's ability
            to participate in the investment structure. Penning reserves the
            right to terminate this Agreement with immediate effect if continued
            cooperation becomes unlawful or materially compromised. This
            includes any losses or liabilities resulting from customer-directed
            orders executed via Penning's Broker Services.
          </p>
          <p className="mb-4">
            9.2 Penning, including its directors, employees, affiliated
            companies, partners, and service providers, shall under no
            circumstances be held liable for any losses, liabilities, taxes,
            penalties, or costs incurred as a result of the Client's
            non-compliance with local laws or reporting obligations.
          </p>
          <p className="mb-4">
            9.3 The Client accepts that it is their sole responsibility to
            declare and report any profits, losses, or capital gains arising
            from the investment to relevant tax or regulatory authorities.
            Penning does not provide tax advice and shall not be held liable for
            any errors, omissions, or costs arising in connection with the
            Client's tax affairs.
          </p>
          <p className="mb-4">
            9.4 The Client agrees to indemnify and hold Penning harmless from
            any claims, damages, or liabilities arising from the Client's breach
            of applicable laws, this Agreement, or representations made herein.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            10. Confidentiality, Data Protection, and Anti-Money Laundering
          </h2>
          <h3 className="text-lg font-medium mt-6 mb-3">
            10.1 Confidentiality
          </h3>
          <p className="mb-4">
            10.1.1 Penning shall treat all non-public information relating to
            the Client's identity, investments, and financial matters as
            confidential and shall not disclose such information without the
            Client's prior written consent, unless required by applicable law or
            regulation.
          </p>
          <p className="mb-4">
            10.1.2 The Client consents to Penning using anonymized or aggregated
            investment data for internal analysis, research, or general
            marketing purposes, provided such use does not identify the Client
            directly or indirectly.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            10.2 Data Protection
          </h3>
          <p className="mb-4">
            10.2.1 By entering into this Agreement, the Client consents to the
            collection, processing, and storage of personal data by Penning, its
            subsidiaries, parent or sister companies, and any third-party
            service providers involved in the execution of this Agreement.
          </p>
          <p className="mb-4">
            10.2.2 Penning acts as data controller for the purposes of
            applicable data protection laws, including the General Data
            Protection Regulation (EU) 2016/679 ("GDPR"). All personal data is
            processed in accordance with Penning's data policy.
          </p>
          <p className="mb-4">
            10.2.3 Sensitive personal data may be stored as long as necessary
            for the administration of the Client relationship and shall be
            deleted when no longer required. Identity documentation is stored
            for a minimum of five years after termination of the relationship,
            in accordance with applicable AML regulations.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            10.3 Legal Disclosure Obligations
          </h3>
          <p className="mb-4">
            10.3.1 The Client authorizes Penning to disclose personal or
            investment-related information without prior notice where such
            disclosure is required by applicable law, regulation, regulatory
            authority, or market rules.
          </p>
          <p className="mb-4">
            10.3.2 This includes disclosure to financial institutions or third
            parties who are subject to the same regulatory requirements, such as
            under the Anti-Money Laundering Act or MiCA.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            10.4 Anti-Money Laundering (AML) and Risk Assessment
          </h3>
          <p className="mb-4">
            10.4.1 Penning is subject to anti-money laundering legislation and
            is required to perform due diligence on all Clients, including
            identity verification and transaction monitoring.
          </p>
          <p className="mb-4">
            10.4.2 The Client consents to Penning sharing identity information
            with financial institutions or partners for AML purposes. If Penning
            suspects any activity related to money laundering or the financing
            of terrorism, Penning is obligated to report such suspicions to the
            relevant authorities, including the State Prosecutor for Serious
            Economic and International Crime (SØIK), without informing the
            Client.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            11. Intellectual Property and Confidential Information
          </h2>
          <p className="mb-4">
            11.1 All intellectual property rights, including but not limited to
            copyrights, trademarks, proprietary algorithms, investment
            strategies, trading systems, software, robots, programs, models,
            reports, and documentation developed or used by Penning in the
            performance of this Agreement (collectively, "Penning IP"), shall
            remain the exclusive property of Penning.
          </p>
          <p className="mb-4">
            11.2 The Client acknowledges that no rights, licenses, or ownership
            interests in any Penning IP are granted or transferred to the Client
            under this Agreement. The Client shall not reproduce, copy, modify,
            reverse engineer, or otherwise exploit any Penning IP.
          </p>
          <p className="mb-4">
            11.3 Any materials or tools made available to the Client by Penning
            are provided solely for the purpose of executing the Client's
            investment mandate and may not be disclosed or shared with third
            parties without Penning's prior written consent.
          </p>
          <p className="mb-4">
            11.4 The Client acknowledges that all Penning IP, technical
            know-how, and business processes constitute confidential business
            information and trade secrets. Unauthorized use or disclosure may
            cause Penning substantial and irreparable harm.
          </p>
          <p className="mb-4">
            11.5 The Client agrees to maintain strict confidentiality with
            respect to all non-public information received from Penning,
            including information relating to investment strategies, portfolio
            composition, fee structures, and performance metrics. This
            obligation shall survive termination of this Agreement.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            12. Amendments, Termination and Breach
          </h2>
          <h3 className="text-lg font-medium mt-6 mb-3">12.1 Amendments</h3>
          <p className="mb-4">
            12.1.1 Penning may amend the terms of this Agreement at any time to
            reflect changes in services, operational procedures, or regulatory
            requirements. All amendments will be published on Penning's platform
            or otherwise communicated to the Client. Continued use of Penning's
            services after any amendment shall constitute the Client's
            acceptance of the updated terms.
          </p>
          <p className="mb-4">
            If the Client does not accept the amended terms, the Client may
            choose to discontinue the use of Penning's services and request
            account closure in accordance with §12.2.2
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.2 Duration and Termination by the Client
          </h3>
          <p className="mb-4">
            12.2.1 This Agreement remains in force for as long as the Client
            maintains an active account with Penning.
          </p>
          <p className="mb-4">
            12.2.2 The Client may terminate the Agreement at any time by
            requesting closure of their account. Termination will be subject to
            the Client settling any outstanding balances, compliance checks, and
            return of assets.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.3 Termination by Penning
          </h3>
          <p className="mb-4">
            12.3.1 Penning may terminate the Agreement or suspend access to
            services with immediate effect if:
          </p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>The Client commits a material breach of the Agreement;</li>
            <li>The Client fails to fulfill payment obligations;</li>
            <li>
              There is a suspicion of illegal activity, including fraud or money
              laundering;
            </li>
            <li>Regulatory obligations require Penning to do so;</li>
            <li>The Client fails to provide required updates (see §12.7).</li>
          </ul>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.4 Breach and Remediation
          </h3>
          <p className="mb-4">
            12.4.1 Either party may terminate the Agreement if the other commits
            a material breach. The breaching party shall be given thirty (30)
            days to remedy the breach before termination takes effect, unless
            immediate termination is justified.
          </p>
          <p className="mb-4">
            12.4.2 Failure to fulfill payment obligations shall always
            constitute a material breach.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.5 AML and Compliance Termination
          </h3>
          <p className="mb-4">
            12.5.1 Penning may suspend or terminate the Agreement immediately in
            case of suspicion or detection of money laundering, terrorist
            financing, or other financial crimes.
          </p>
          <p className="mb-4">
            12.5.2 In such cases, Penning may freeze or delay withdrawals until
            a final administrative or judicial resolution is reached. The Client
            bears all related risks and costs.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.6 Reporting to Authorities
          </h3>
          <p className="mb-4">
            12.6.1 Penning reserves the right to report suspicious activity to
            relevant authorities, including internal and external AML spot
            checks, without informing the Client.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.7 Client Notification Obligations
          </h3>
          <p className="mb-4">
            12.7.1 The Client must promptly notify Penning of any changes to
            ownership, legal structure, management, contact details, or any
            other information relevant to compliance or the client relationship.
          </p>
          <p className="mb-4">
            12.7.2 Failure to do so shall constitute a material breach. Penning
            may deduct any losses or costs incurred as a result from the
            Client's managed capital.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.8 Reporting of Misuse and Irregularities
          </h3>
          <p className="mb-4">
            12.8.1 If Penning detects or suspects abuse, misuse of services, or
            violation of law or regulation, it may report such activity to
            authorities without prior notice to the Client. This includes
            internal or external investigations.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.9 Suspension or Termination Due to Suspicion of Illegality
          </h3>
          <p className="mb-4">
            12.9.1 Penning may suspend or terminate the Agreement immediately if
            there is a justified suspicion of abuse, fraud, or use of Penning's
            services in breach of applicable law.
          </p>
          <p className="mb-4">
            12.9.2 All resulting losses, costs, or liabilities are borne solely
            by the Client.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            12.10 Survival of Terms
          </h3>
          <p className="mb-4">
            12.10.1 Provisions relating to confidentiality, liability, tax
            responsibilities, and governing law shall survive the termination of
            this Agreement.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            13. Governing Law and Dispute Resolution
          </h2>
          <p className="mb-4">
            13.1 This Agreement shall be governed by and construed in accordance
            with the laws of Denmark.
          </p>
          <p className="mb-4">
            13.2 In the event of any dispute arising out of or in connection
            with this Agreement, the Parties shall first seek to resolve the
            matter amicably through good-faith negotiations.
          </p>
          <p className="mb-4">
            13.3 If no amicable solution can be reached, any legal dispute shall
            be brought before the competent courts of Denmark. If the Client
            qualifies as a consumer under Danish law, venue shall be determined
            in accordance with the rules of the Danish Consumer Ombudsman and
            applicable consumer protection legislation.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            14. Miscellaneous Provisions
          </h2>
          <h3 className="text-lg font-medium mt-6 mb-3">14.1 Severability</h3>
          <p className="mb-4">
            14.1.1 If any provision of this Agreement is held to be illegal,
            invalid, or unenforceable under applicable law, the remaining
            provisions shall remain in full force and effect. The Parties agree
            to replace any such provision with a valid clause that most closely
            reflects the Agreement's original intent.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">14.2 Language</h3>
          <p className="mb-4">
            14.2 The Client confirms that they are proficient in the English
            language and agree that all communication, documentation, and
            contractual interpretation may be conducted in English.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            14.3 Entire Agreement
          </h3>
          <p className="mb-4">
            14.3.1 This Agreement constitutes the entire agreement between the
            Parties and supersedes all prior discussions, representations, or
            agreements, whether written or oral, regarding the subject matter
            hereof.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">14.4 Waiver</h3>
          <p className="mb-4">
            14.4.1 Failure by either Party to exercise any right or remedy under
            this Agreement shall not constitute a waiver of that or any other
            right or remedy.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            14.5 Binding Effect and Assignment
          </h3>
          <p className="mb-4">
            14.5.1 This Agreement is binding upon the Parties and their
            respective successors and permitted assigns. The Client may not
            assign or transfer any of their rights or obligations under this
            Agreement without Penning's prior written consent and successful
            completion of Penning's onboarding and compliance procedures.
            Penning may delegate performance of its obligations, in whole or in
            part, to subcontractors or affiliated entities, provided that
            Penning remains responsible for compliance with applicable law.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            14.6 Tax Responsibility
          </h3>
          <p className="mb-4">
            14.6.1 The Client is solely responsible for obtaining tax advice and
            for reporting any returns, losses, or related matters to the
            relevant tax authorities. Penning does not provide tax advice and
            shall not be held liable for the Client's tax treatment or
            compliance. Penning will only disclose tax-related information to
            authorities when legally required to do so.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            15. Client Rights in Case of Loss or Insolvency
          </h2>
          <p className="mb-4">
            15.1 In the event of loss, theft, cyberattack, or insolvency of a
            third-party custodian or service provider, the Client acknowledges
            that Penning shall take all reasonable steps to secure recovery of
            assets on behalf of the Client.
          </p>
          <p className="mb-4">
            15.2 Penning does not guarantee the return of digital assets in such
            events but will facilitate claims, participate in recovery
            processes, and communicate openly with the Client. Any recovery will
            be distributed proportionally to affected Clients based on
            documented ownership at the time of the incident.
          </p>
          <p className="mb-4">
            15.3 The Client accepts that digital asset investments involve
            technology-specific risks that may not be recoverable through
            traditional legal mechanisms. These risks are described in section 5
            of this Agreement.
          </p>

          <h2 className="text-xl font-semibold mt-8 mb-4">
            16. Custody and Asset Safeguarding
          </h2>
          <h3 className="text-lg font-medium mt-6 mb-3">
            16.1 Custody Structure
          </h3>
          <p className="mb-4">
            16.1.2 Penning ensures that all client assets are held in
            individually segregated wallets or subaccounts maintained with
            regulated or reputable third-party custodians. Client assets are not
            held on Penning's own balance sheet and are legally and
            operationally separated from Penning's proprietary funds.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            16.2 Third-Party Custodians
          </h3>
          <p className="mb-4">
            16.2.1 Penning selects third-party custodians based on commercial,
            technical, and regulatory criteria. These providers are subject to
            initial and ongoing due diligence, including assessments of their
            financial stability, technological security, and compliance with
            relevant regulatory requirements.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            16.3 Safeguarding Procedures
          </h3>
          <p className="mb-4">
            16.3.1 Penning maintains an internal Custody and Asset Safeguarding
            Policy that outlines procedures for the secure storage, handling,
            and protection of client assets. This includes:
          </p>
          <ul className="list-disc pl-8 mb-4 space-y-2">
            <li>Internal controls and segregation of duties.</li>
            <li>Risk management processes and contingency planning.</li>
            <li>Regular reconciliation of custody positions.</li>
            <li>Cybersecurity measures aligned with best practices.</li>
            <li>
              Insurance arrangements (if applicable) to protect against specific
              risks related to custody.
            </li>
          </ul>

          <h3 className="text-lg font-medium mt-6 mb-3">
            16.4 Transparency and Reporting
          </h3>
          <p className="mb-4">
            16.4.1 Clients can monitor their assets via Penning's digital
            dashboard, showing real-time updates on portfolio composition and
            custody status. Penning also maintains internal records of each
            client's holdings and movements to ensure compliance with asset
            segregation requirements.
          </p>

          <h3 className="text-lg font-medium mt-6 mb-3">
            16.5 Limitation of Liability
          </h3>
          <p className="mb-4">
            16.5.1 Although Penning takes commercially reasonable measures to
            protect client assets, it does not guarantee the financial standing
            or operational integrity of third-party custodians. Clients
            acknowledge that risks related to third-party failures,
            cybersecurity threats, or market disruptions cannot be fully
            eliminated.
          </p>
        </div>
      </div>

      <div className="p-6 pt-4 flex justify-center border-t border-black/5 dark:border-white/10">
        <div className="max-w-[600px] w-full">
          {!hasScrolledToBottom && (
            <div className="text-amber-600 dark:text-amber-400 text-sm mb-3 text-center">
              Please scroll through the entire agreement before accepting
            </div>
          )}
          <FormButton
            title="I Accept"
            onClick={onAccept}
            isLoading={isLoading}
            loadingText="Processing…"
            disabled={!hasScrolledToBottom}
          />
        </div>
      </div>
    </DefaultModal>
  )
}

export default ClientAgreementModal
